import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useGhgOrganization, useLanguage } from 'src/common/hooks';
import { useRabenConsent } from 'src/Ghg/hooks';
import { DeclinePayload } from 'src/Ghg/interfaces';
import PageLoading from 'src/components/PageLoading';
import DeclineReasonForm from '../components/DeclineReasonForm';
import { downloadSupplierCommitmentPdf } from './PdfDocument';
import {
  formatStatementDate,
  resolveStatementLanguage,
} from './statementFormat';
import TargetArea from './TargetArea';
import { ActionsRow, ConfirmationBox, PageContainer } from './index.styles';

// pdfStamp.ts refuses to stamp a value that does not fit its blank (a long
// company name in the 3 lines of "Podpis" at 7 pt) instead of drawing a
// clipped one into a document the carrier signs. That is the one failure the
// carrier can understand and act on, so it gets a message of its own; any
// other failure (network, a missing template) is not swallowed.
const isPdfValueTooLongError = (error: unknown): boolean =>
  /does not fit its blank/.test(
    String((error as { message?: unknown } | null)?.message ?? '')
  );

// C16 (specs/001-raben-country-masteradmin/mass-specs/specs/20260826-decl-
// commitment-tab-pdf.md, Goal 1/2). BR-2: reachable regardless of
// CommitmentWindow -- the window (C15) only gates the pop-up
// (RabenConsentModal), never this tab. Renders one of three states off the
// same `consent` tri-state the pop-ups already read:
//   - null   -> statement + Letter link + Accept/Reject (Goal 2b)
//   - true   -> accepted confirmation + "download declaration" (Goal 2a)
//   - false  -> declined state, Accept still possible (Goal 2c)
//
// RG 28.08 D2: only the carrier's own users decide (`is_direct_member`,
// strict -- a 403 from the POST redirects to /403). Anyone else (e.g. a
// Raben admin looking at a carrier) gets the same state read-only: no
// Accept/Reject, just what was decided. RG 28.08 1.3, D3: Reject first opens
// the optional reason form; D4: after a refusal the tab shows the reason and
// the Letter, and Accept stays available.
export default function SupplierCommitment() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.supplierCommitment',
  });
  // The reason labels live in the shared `ghg.declineReason` block.
  const { t: tRoot } = useTranslation();
  const organization = useGhgOrganization();
  const { language } = useLanguage();
  const { data, isLoading, accept, decline } = useRabenConsent(organization.id);
  const [downloading, setDownloading] = React.useState(false);
  const [declining, setDeclining] = React.useState(false);

  // Client directive 2026-08-27 (supersedes the DOC's auto-download
  // reading): accepting no longer downloads the file by itself -- the tab
  // re-renders into its accepted state right here, where the download
  // button is, and the carrier clicks it when they want the letter.
  const handleAccept = async () => {
    await accept.mutateAsync();
  };

  const handleDecline = async (payload: DeclinePayload) => {
    await decline.mutateAsync(payload);
    setDeclining(false);
  };

  // Goal 2a: "Pobierz oświadczenie (PDF)" -- regenerates the SAME file
  // deterministically from the stored snapshot, not from live org data.
  const handleRedownload = async () => {
    if (!data?.snapshot) return;
    setDownloading(true);
    try {
      await downloadSupplierCommitmentPdf({
        snapshot: data.snapshot,
        language,
      });
    } catch (error) {
      if (!isPdfValueTooLongError(error)) throw error;
      toast.error(t('pdfTooLong') as string);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading || !data) {
    return <PageLoading />;
  }

  const canDecide = data.is_direct_member === true;

  // The same date format as the readiness checklist below (01.10.2026), not
  // the browser's default locale format.
  const acceptedOnDate = data.snapshot
    ? formatStatementDate(
        resolveStatementLanguage(language),
        data.snapshot.accepted_on
      )
    : null;

  // "Powód: <label>" (+ the free text of "other"). An unknown code falls back
  // to the raw code rather than an empty label.
  const reasonCode = data.decline_reason;
  const reasonText = data.decline_reason_text;
  const reasonLine = reasonCode
    ? `${tRoot('ghg.declineReason.reasonPrefix')} ${tRoot(
        `ghg.declineReason.codes.${reasonCode}`,
        { defaultValue: reasonCode }
      )}${reasonText ? ` – ${reasonText}` : ''}`
    : null;

  const letterLink = (
    <Link href={t('letterUrl')} target="_blank" rel="noopener noreferrer">
      {t('letterLinkLabel')}
    </Link>
  );

  return (
    <PageContainer>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('title')}
      </Typography>

      {!canDecide && (
        <Alert severity="info" role="status" sx={{ mb: 3 }}>
          {t('readOnlyNote')}
        </Alert>
      )}

      {data.consent === true && (
        <ConfirmationBox>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {/* D2: the read-only view (Raben admin) must not say "you". */}
            {canDecide
              ? t('acceptedStatement')
              : t('acceptedStatementReadOnly')}
          </Typography>
          {acceptedOnDate && data.snapshot && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {/* Goal 2a: "(date, by-whom if available)" -- no individual
                  user is tracked anywhere in this flow, but the snapshot's
                  company name IS the "by-whom" this data model actually
                  has, so it is shown rather than omitted. */}
              {t('acceptedOn', {
                date: acceptedOnDate,
                company: data.snapshot.company_name,
              })}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleRedownload}
            disabled={downloading}
          >
            {t('downloadButton')}
          </Button>
        </ConfirmationBox>
      )}

      {data.consent === null && (
        <Box>
          {!canDecide && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('noDecisionStatement')}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('statement')}
          </Typography>
          {letterLink}
          {canDecide &&
            (declining ? (
              <DeclineReasonForm
                context="letter"
                options={data.decline_reason_options}
                loading={decline.isLoading}
                onConfirm={handleDecline}
                onCancel={() => setDeclining(false)}
              />
            ) : (
              <ActionsRow>
                <Button
                  variant="text"
                  onClick={() => setDeclining(true)}
                  disabled={accept.isLoading}
                >
                  {t('reject')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAccept}
                  disabled={accept.isLoading || downloading}
                >
                  {t('accept')}
                </Button>
              </ActionsRow>
            ))}
        </Box>
      )}

      {data.consent === false && (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {canDecide
              ? t('declinedStatement')
              : t('declinedStatementReadOnly')}
          </Typography>
          {reasonLine && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {reasonLine}
            </Typography>
          )}
          {letterLink}
          {canDecide && (
            <ActionsRow>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={accept.isLoading || downloading}
              >
                {t('accept')}
              </Button>
            </ActionsRow>
          )}
        </Box>
      )}

      {/* RG 28.08 2.3 (K7): always the last child. TargetArea decides by
          itself whether there is anything to show (header, readiness
          checklist, TargetSection). */}
      <TargetArea />
    </PageContainer>
  );
}
